import React, { useMemo } from 'react';
import { Icons } from '../Icons';

/**
 * Toggle — On/off switch with optional label.
 *
 * Styled as a track with a sliding thumb. When checked, the thumb
 * slides to the right and shows a checkmark icon. Uses a hidden
 * native checkbox for accessibility and keyboard support (Space to toggle).
 *
 * @param {boolean} checked   - Whether the toggle is on
 * @param {boolean} disabled  - Disabled state
 * @param {func}    onChange   - Change handler
 * @param {string}  label     - Optional label text
 */
export const Toggle = ({
    checked = false,
    disabled = false,
    onChange,
    label,
    className = "",
    ...props
}) => {
    const computedClass = useMemo(() =>
        [
            "sc-toggle",
            checked ? "sc-checked" : "",
            disabled ? "sc-disabled" : "",
            className
        ].filter(Boolean).join(" "),
        [checked, disabled, className]
    );

    return (
        <label className={computedClass}>
            <div className="sc-control-wrapper">
                {/* Hidden native checkbox handles form state and keyboard interaction */}
                <input 
                    type="checkbox" 
                    checked={checked} 
                    disabled={disabled}
                    onChange={onChange}
                    {...props}
                />
                {/* Track: the pill-shaped background */}
                <div className="sc-track">
                    {/* Thumb: the sliding circle with optional check icon */}
                    <div className="sc-thumb">
                        {checked && (
                            <Icons name="check" size={24} />
                        )}
                    </div>
                </div>
            </div>
            {label && <span className="sc-label body-m-semibold">{label}</span>}
        </label>
    );
};
